<template>
  <div class="fulltext-search">
    <v-menu
      v-model="showResults"
      transition="scale-transition"
      offset-y
      content-class="elevation-0"
      :close-on-content-click="false"
      :nudge-top="5"
    >
      <template v-slot:activator="{ args }">
        <v-text-field
          v-model="searchKeyword"
          v-bind="args"
          hide-details
          append-icon="mdi-magnify"
          :placeholder="$t('jobs.fulltextSearch')"
          :aria-label="$t('jobs.fulltextSearch')"
          :filled="filled"
          outlined
          class="rounded"
          :class="{ bordered: bordered }"
          @keyup="search"
          @keydown.down.prevent="focusNext"
          @keydown.up.prevent="focusPrevious"
        ></v-text-field>
      </template>
      <div
        class="fulltext-search__search-results-wrapper"
        @keydown.down.prevent="focusNext"
        @keydown.up.prevent="focusPrevious"
      >
        <v-sheet
          class="fulltext-search__search-results"
          id="fulltext-search-results"
        >
          <div class="text-right text--gray">
            {{ numSearchResults }} {{ $t('matches') }}
          </div>
          <div
            v-for="(item, index) in displayedSearchResults"
            :key="`fulltext_result_${item.jobofferId}`"
          >
            <a href="" @click="navigateToJob(item)" :tabindex="index + 1">
              {{ localize(item.name) }}
            </a>
          </div>

          <div class="d-flex mt-2">
            <div v-if="hasPreviousItems">
              <v-btn
                small
                plain
                text
                class="px-0"
                :ripple="false"
                @click="goToPrevious"
              >
                <v-icon>mdi-chevron-left</v-icon>
                {{ $t('previous') }}
              </v-btn>
            </div>
            <div v-if="hasNextItems" class="d-flex flex-grow-1 justify-end">
              <v-btn
                small
                plain
                text
                class="px-0"
                :ripple="false"
                @click="goToNext"
              >
                {{ $t('next') }}
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>
        </v-sheet>
      </div>
    </v-menu>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import TrackingService from '@/services/TrackingService.js';

export default {
  props: {
    filled: {
      type: Boolean,
      required: false,
      default: false
    },

    bordered: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data: () => ({
    showResults: false,
    searchKeyword: '',
    pageIndex: 0,
    focusIndex: -1,
    searchDebounce: null
  }),

  computed: {
    ...mapState('jobsModule', {
      joboffers: 'joboffers'
    }),

    ...mapState('companyModule', {
      showExternalAdvert: 'showExternalAdvert',
      showBlacklistedJoboffers: 'showBlacklistedJoboffers'
    }),

    ...mapState({
      currentLanguage: 'currentLanguage'
    }),

    hasPreviousItems() {
      return this.numPages > 0 && this.pageIndex > 0;
    },

    hasNextItems() {
      return this.numPages > 0 && this.pageIndex < this.numPages - 1;
    },

    searchResults() {
      const kw = this.searchKeyword.toLowerCase();
      const results = this.visibleJoboffers.filter(
        (x) => this.localize(x.name).toLowerCase().indexOf(kw) >= 0
      );

      const sortedResults = results.sort((a, b) =>
        this.localize(a.name).localeCompare(this.localize(b.name))
      );

      return sortedResults;
    },

    numPages() {
      return Math.ceil(this.numSearchResults / this.numResults);
    },

    numResults() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
        case 'sm':
        case 'md':
          return 5;
        case 'lg':
          return 15;
        case 'xl':
          return 15;
        default:
          return 5;
      }
    },

    numSearchResults() {
      return this.searchResults.length;
    },

    displayedSearchResults() {
      const startItem = this.pageIndex * this.numResults;
      const endItem = startItem + this.numResults;
      return this.searchResults.slice(startItem, endItem);
    },

    visibleJoboffers() {
      return this.joboffers.filter(
        (x) => this.showBlacklistedJoboffers || !x.isBlacklisted
      );
    }
  },

  methods: {
    clearSearch() {
      this.showResults = false;
      this.searchKeyword = '';
      this.clearSearchTrackingDebounce();
    },

    goToNext() {
      if (this.pageIndex < this.numPages - 1) {
        this.pageIndex++;
      }
    },

    goToPrevious() {
      if (this.pageIndex > 0) {
        this.pageIndex--;
      }
    },

    navigateToJob(joboffer) {
      if (this.showExternalAdvert) {
        const advertUrl = `${joboffer.advertUrl}?lang=${this.currentLanguage}`;
        window.open(advertUrl, '_blank');
      } else {
        this.$router.push({
          name: 'jobDetails',
          params: {
            jobId: joboffer.jobofferId
          }
        });
      }

      this.clearSearch();
    },

    search() {
      if (!this.searchKeyword || this.searchKeyword.length < 3) {
        this.showResults = false;
        this.clearSearchTrackingDebounce();
        return;
      }

      this.focusIndex = -1;
      this.showResults = true;
      this.scheduleSearchTracking();
    },

    focusNext() {
      const wrapper = document.querySelector('#fulltext-search-results');
      const links = wrapper.querySelectorAll('a');

      if (this.focusIndex >= links.length - 1) {
        this.focusIndex = 0;
      } else {
        this.focusIndex++;
      }

      links[this.focusIndex].focus();
    },

    focusPrevious() {
      const wrapper = document.querySelector('#fulltext-search-results');
      const links = wrapper.querySelectorAll('a');

      if (this.focusIndex <= 0) {
        this.focusIndex = links.length - 1;
      } else {
        this.focusIndex--;
      }

      links[this.focusIndex].focus();
    },

    clearSearchTrackingDebounce() {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = null;
      }
    },

    scheduleSearchTracking() {
      this.clearSearchTrackingDebounce();

      this.searchDebounce = setTimeout(() => {
        this.searchDebounce = null;

        const keyword = this.searchKeyword;
        if (!keyword || keyword.length < 3) {
          return;
        }

        const resultsCount = this.numSearchResults;
        TrackingService.trackJobofferSearch(keyword, resultsCount);
      }, 500);
    }
  }
};
</script>
